// Between the card the server sends and the saveRouteCard input the editor
// sends back. Every save carries the whole card — settings and points — so a
// save can never half-apply an edit made while an earlier one was in flight.
//
// No time zone is sent: the server works out the hike's own zone from where it
// starts. The browser's zone is wherever the planner happens to be.
import { toCheckpointInput, type EditableCheckpoint } from './checkpoints';

export interface CardSettingsSource {
  paceKmh: number;
  climbMinutesPer100m: number;
  startDate: string | null;
  startMinutes: number;
  gridCrs: string | null;
  partySize: number | null;
  partyNames: string | null;
  leaderMobile: string | null;
}

export interface EditableSettings {
  paceKmh: number;
  climbMinutesPer100m: number;
  startDate: string | null;
  startMinutes: number;
  gridCrs: string | null;
  partySize: number | null;
  partyNames: string;
  leaderMobile: string;
}

export function settingsFromCard(card: CardSettingsSource): EditableSettings {
  return {
    paceKmh: card.paceKmh,
    climbMinutesPer100m: card.climbMinutesPer100m,
    startDate: card.startDate,
    startMinutes: card.startMinutes,
    gridCrs: card.gridCrs,
    partySize: card.partySize,
    partyNames: card.partyNames ?? '',
    leaderMobile: card.leaderMobile ?? '',
  };
}

/** A party size from a number input: a whole number 1-99, or null when cleared. */
export function partySizeFrom(value: string | number | null): number | null {
  const parsed = typeof value === 'number' ? value : Number.parseInt(String(value ?? ''), 10);
  if (!Number.isFinite(parsed) || parsed < 1) return null;
  return Math.min(Math.round(parsed), 99);
}

export function buildSaveInput(
  hikeDayId: string,
  settings: EditableSettings,
  points: EditableCheckpoint[],
) {
  return {
    hikeDayId,
    checkpoints: toCheckpointInput(points),
    paceKmh: settings.paceKmh,
    climbMinutesPer100m: settings.climbMinutesPer100m,
    startDate: settings.startDate || null,
    startMinutes: settings.startMinutes,
    gridCrs: settings.gridCrs,
    partySize: settings.partySize,
    partyNames: settings.partyNames.trim() || null,
    leaderMobile: settings.leaderMobile.trim() || null,
  };
}
